import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { FilterDescriptor } from '@progress/kendo-react-dropdowns/dist/npm/common/filterDescriptor';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { GLCodePeriodDepartment } from 'src/services/useGlCodeDepartmentApi';

interface DepartmentDropdownProps {}

const DepartmentDropdown: FC<DepartmentDropdownProps> = () => {
  const { t } = useTranslation();

  const {
    departmentList,
    selectedPeriodDepartment,
    selectedYearDepartment,
    setSelectedPeriodDepartment,
    setSelectedYearDepartment,
    confirmBeforeProceeding,
    setPeriodCheckbookDataState,
    setYearCheckbookDataState,
    isPeriodView,
    glCodeDepartments,
  } = useViewCheckbook();

  const [data, setData] = useState(departmentList);

  const setDepartment = useCallback(
    (department: GLCodePeriodDepartment) => {
      isPeriodView
        ? setSelectedPeriodDepartment(department)
        : setSelectedYearDepartment(department);
      setPeriodCheckbookDataState((prev) => ({
        ...prev,
        skip: 0,
      }));
      setYearCheckbookDataState((prev) => ({
        ...prev,
        skip: 0,
      }));
    },
    [
      isPeriodView,
      setPeriodCheckbookDataState,
      setSelectedPeriodDepartment,
      setSelectedYearDepartment,
      setYearCheckbookDataState,
    ]
  );

  useEffect(() => {
    if (departmentList.length === 2 && glCodeDepartments.clientHasDepartment) {
      setData([departmentList[1]]);
      setDepartment(departmentList[1]);
    } else {
      setData(departmentList);
    }
  }, [departmentList, glCodeDepartments.clientHasDepartment, setDepartment]);

  const filterData = useCallback(
    (filter: FilterDescriptor | CompositeFilterDescriptor) => {
      let data = [...departmentList];
      if (departmentList.length === 2) {
        data = data.filter((item) => item.departmentId !== 0);
      }
      return filterBy([...data], filter);
    },
    [departmentList]
  );
  const filterChange = useCallback(
    (event: DropDownListFilterChangeEvent) => {
      setData(filterData(event.filter));
    },
    [filterData]
  );

  const handleDepartmentChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e.target;
      confirmBeforeProceeding(() => {
        setDepartment(value);
      });
    },
    [confirmBeforeProceeding, setDepartment]
  );

  return (
    <div className="flex items-center xl:gap-2 flex-wrap">
      <p className="text-xs">{t('Select a Department')}</p>
      <DropDownList
        data={data}
        onChange={handleDepartmentChange}
        value={isPeriodView ? selectedPeriodDepartment : selectedYearDepartment}
        textField="departmentName"
        dataItemKey="departmentId"
        style={{ width: '250px' }}
        filterable
        onFilterChange={filterChange}
      />
    </div>
  );
};

export default DepartmentDropdown;
